import DateInput from '../../components/DateInput';
/**
 * Admin Fitness Assessment — record + view fitness assessments for a client.
 *
 * Lives inside `/admin/fitness` (queries `client_id` from URL) at
 * `/admin/fitness/clients/:client_id/assessments`.
 *
 * Sections:
 *   1) **New assessment form** — trainer enters body metrics + fitness tests.
 *   2) **Progression chart** — line chart of key metrics over time.
 *   3) **Training program builder** — picks exercises from the open-source
 *      `yuhonas/free-exercise-db` library, with sets/reps/rest per exercise.
 *
 * Two **separate** printable PDFs (user's choice):
 *   • Print assessment — body metrics + chart + goals.
 *   • Print training program — exercise list with sets/reps/rest/notes.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { ChevronLeft, Plus, Save, X, Printer, TrendingUp, FileText, Loader2, ClipboardList, Dumbbell, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { printHtml, signatureBlock } from '../../lib/print';
import ExercisePicker from '../../components/ExercisePicker';
import { safeConfirm } from '../../lib/confirm';

const METRICS = [
  { k: 'weight_kg',                label: 'Weight (kg)',         unit: 'kg',  type: 'number', step: '0.1' },
  { k: 'body_fat_pct',             label: 'Body fat (%)',        unit: '%',   type: 'number', step: '0.1' },
  { k: 'resting_hr',               label: 'Resting HR (bpm)',    unit: 'bpm', type: 'number', step: '1' },
  { k: 'blood_pressure_sys',       label: 'BP systolic',         unit: 'mmHg',type: 'number', step: '1' },
  { k: 'blood_pressure_dia',       label: 'BP diastolic',        unit: 'mmHg',type: 'number', step: '1' },
  { k: 'vo2_max',                  label: 'VO₂ max',             unit: 'ml/kg/min', type: 'number', step: '0.1' },
  { k: 'flexibility_sit_reach_cm', label: 'Sit-and-reach (cm)',  unit: 'cm',  type: 'number', step: '0.5' },
  { k: 'grip_strength_kg',         label: 'Grip strength (kg)',  unit: 'kg',  type: 'number', step: '0.5' },
  { k: 'pushup_count',             label: 'Push-ups (reps)',     unit: 'reps',type: 'number', step: '1' },
  { k: 'plank_seconds',            label: 'Plank hold (sec)',    unit: 's',   type: 'number', step: '1' },
];

const CHARTABLE = ['weight_kg', 'body_fat_pct', 'resting_hr', 'vo2_max', 'pushup_count', 'plank_seconds'];

export default function FitnessAssessment() {
  const { client_id } = useParams();
  const [client, setClient] = useState(null);
  const [rows, setRows] = useState([]);
  const [program, setProgram] = useState(null);  // { exercises[], title, notes, week_starts_on }
  const [programDirty, setProgramDirty] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingProgram, setSavingProgram] = useState(false);
  const [emailingProgram, setEmailingProgram] = useState(false);

  const load = () => {
    api.get('/fitness/clients').then(r => setClient((r.data || []).find(c => c.client_id === client_id) || null));
    api.get('/fitness/assessments', { params: { client_id } }).then(r => setRows(r.data || [])).catch(()=>{});
    api.get('/fitness/training-programs', { params: { client_id } }).then(r => {
      const latest = (r.data || [])[0];
      setProgram(latest || { client_id, title: 'Training program', exercises: [], notes: '' });
      setProgramDirty(false);
    }).catch(()=>{
      setProgram({ client_id, title: 'Training program', exercises: [], notes: '' });
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client_id]);

  const last = rows[0]; // sorted DESC by date

  const saveProgram = async () => {
    if (!program) return;
    setSavingProgram(true);
    try {
      const r = await api.post('/fitness/training-programs', {
        client_id,
        title: program.title || 'Training program',
        week_starts_on: program.week_starts_on,
        notes: program.notes,
        exercises: program.exercises || [],
      });
      setProgram(r.data);
      setProgramDirty(false);
      toast.success('Training program saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSavingProgram(false); }
  };

  const emailProgram = async () => {
    if (!program?.program_id) {
      toast.error('Save the program first before emailing it.');
      return;
    }
    if (programDirty) {
      toast.error('Save your unsaved changes before emailing the program.');
      return;
    }
    if (!client?.email) {
      toast.error('Client has no email address on file.');
      return;
    }
    if (!await safeConfirm(`Send the training program PDF to ${client.email}?`)) return;
    setEmailingProgram(true);
    try {
      const r = await api.post(`/fitness/training-programs/${program.program_id}/send-email`);
      if (r.data.resend_configured === false) {
        toast.message(`Email service not yet configured — the PDF (${Math.round(r.data.attachment_size_bytes/1024)} KB) was rendered but not delivered.`);
      } else {
        toast.success(`Program sent to ${r.data.to}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed.');
    } finally { setEmailingProgram(false); }
  };

  const updateProgram = (updater) => {
    setProgram(p => ({ ...(p || { client_id, exercises: [] }), ...(typeof updater === 'function' ? updater(p) : updater) }));
    setProgramDirty(true);
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="mb-6">
        <Link to="/admin/fitness" className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand" data-testid="back-to-fitness">
          <ChevronLeft className="w-3 h-3"/> Back to Fitness
        </Link>
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mt-2 mb-1">WELLNESS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight inline-flex items-center gap-3">
          <TrendingUp className="w-7 h-7 text-brand"/> Fitness assessment
        </h1>
        {client && <p className="text-fg-muted mt-1">{client.full_name} · {client.email}</p>}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={()=>setShowNew(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="fa-new"><Plus className="w-4 h-4"/> New assessment</button>
        <button onClick={()=>printAssessment({ client, last, rows })} className="btn-secondary text-sm inline-flex items-center gap-2" disabled={!last} data-testid="fa-print-assessment"><ClipboardList className="w-4 h-4"/> Print assessment</button>
        <button onClick={()=>printProgram({ client, last, program })} className="btn-secondary text-sm inline-flex items-center gap-2" disabled={!(program?.exercises?.length)} data-testid="fa-print-program"><FileText className="w-4 h-4"/> Print training program</button>
        <button onClick={emailProgram} className="btn-secondary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                disabled={emailingProgram || !program?.program_id || programDirty || !client?.email}
                title={!program?.program_id ? 'Save the program first' : programDirty ? 'Save unsaved changes first' : !client?.email ? 'Client has no email' : ''}
                data-testid="fa-email-program">
          {emailingProgram ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4"/>}
          {emailingProgram ? 'Sending…' : 'Send program by email'}
        </button>
      </div>

      {/* Progression chart — line per metric over time. */}
      <div className="bento-card mb-5" data-testid="fa-chart">
        <h3 className="font-heading font-semibold mb-3">Progression chart</h3>
        {rows.length < 2 ? (
          <p className="text-fg-muted text-sm">At least 2 assessments are needed to show a trend. {rows.length === 0 ? 'Add the first one above.' : 'One more to go.'}</p>
        ) : <ProgressionChart rows={rows}/>}
      </div>

      {/* Training program builder */}
      <div className="bento-card mb-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h3 className="font-heading font-semibold inline-flex items-center gap-2">
              <Dumbbell className="w-4 h-4 text-brand"/> Training program
            </h3>
            <p className="text-xs text-fg-muted mt-1">
              Build a program from the open-source exercise library. Save it to print the patient's sheet.
            </p>
          </div>
          <div className="flex gap-2">
            {programDirty && <span className="text-xs text-[#FFB000] self-center">Unsaved changes</span>}
            <button onClick={saveProgram} disabled={savingProgram || !programDirty}
                    className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="fa-save-program">
              {savingProgram ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
              {savingProgram ? 'Saving…' : 'Save program'}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-2 mb-3">
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Title</div>
            <input className="input !text-sm" value={program?.title || ''}
                   onChange={e => updateProgram({ title: e.target.value })}
                   data-testid="fa-program-title"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Week starting</div>
            <DateInput className="!text-sm" value={program?.week_starts_on || ''}
                   onChange={e => updateProgram({ week_starts_on: e.target.value })}/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">General notes</div>
            <input className="input !text-sm" value={program?.notes || ''}
                   onChange={e => updateProgram({ notes: e.target.value })}
                   placeholder="3 sessions/week, hydrate well, RPE 6/10"/>
          </label>
        </div>

        <ExercisePicker
          value={program?.exercises || []}
          onChange={(next) => updateProgram({ exercises: next })}
          context="fitness"
          clientId={client_id}
          testidPrefix="fa-ep"
        />
      </div>

      {/* Table of past assessments. */}
      <div className="admin-card">
        <h3 className="font-heading font-semibold mb-3">History ({rows.length})</h3>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              {METRICS.map(m => <th key={m.k} className="text-xs">{m.label}</th>)}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.assessment_id}>
                <td className="text-xs whitespace-nowrap">{r.date}</td>
                {METRICS.map(m => <td key={m.k} className="text-xs text-fg-muted">{r[m.k] ?? '—'}</td>)}
                <td className="text-xs text-fg-muted max-w-[140px] truncate" title={r.notes}>{r.notes || ''}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={METRICS.length + 2} className="text-center py-6 text-fg-muted">No assessments yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewAssessmentModal
          client={client}
          onClose={()=>setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
          busy={busy}
          setBusy={setBusy}
        />
      )}
    </div>
  );
}


function ProgressionChart({ rows }) {
  const asc = [...rows].reverse();
  const data = asc.map(r => {
    const point = { date: r.date };
    CHARTABLE.forEach(k => { if (r[k] != null) point[k] = r[k]; });
    return point;
  });
  const showK = CHARTABLE.filter(k => asc.filter(r => r[k] != null).length >= 2);
  if (!showK.length) return <p className="text-fg-muted text-sm">Not enough numeric data yet.</p>;

  const colors = ['#003D80', '#FFB000', '#B83A3A', '#3A664D', '#7A4D9A', '#5A3A00'];

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
        <LineChart data={data} margin={{ left: -5, right: 8, top: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8DFC8"/>
          <XAxis dataKey="date" stroke="#7A827A" fontSize={11}/>
          <YAxis stroke="#7A827A" fontSize={11}/>
          <Tooltip contentStyle={{ background: '#FAF6EE', border: '1px solid #E8DFC8', fontSize: 12 }}/>
          <Legend wrapperStyle={{ fontSize: 11 }}/>
          {showK.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} name={METRICS.find(m => m.k === k)?.label || k}
                  stroke={colors[i % colors.length]} strokeWidth={2}
                  dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls/>
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}


function NewAssessmentModal({ client, onClose, onSaved, busy, setBusy }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    notes: '',
    goals: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!client) return toast.error('No client loaded.');
    setBusy(true);
    try {
      const payload = { client_id: client.client_id, date: form.date, notes: form.notes, goals: form.goals };
      METRICS.forEach(m => {
        const v = form[m.k];
        if (v !== '' && v != null && !Number.isNaN(parseFloat(v))) {
          payload[m.k] = parseFloat(v);
        }
      });
      await api.post('/fitness/assessments', payload);
      toast.success('Assessment recorded.');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-bold">New fitness assessment</h3>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>

        <Field label="Date">
          <DateInput value={form.date} onChange={e=>set('date', e.target.value)} data-testid="fa-date"/>
        </Field>

        <div className="text-xs uppercase tracking-wider text-fg-muted font-medium mt-4 mb-2">Body metrics</div>
        <div className="grid grid-cols-2 gap-3">
          {METRICS.map(m => (
            <Field key={m.k} label={m.label}>
              <input
                type={m.type} step={m.step}
                className="input"
                value={form[m.k] ?? ''}
                onChange={e => set(m.k, e.target.value)}
                placeholder={m.unit}
                data-testid={`fa-input-${m.k}`}
              />
            </Field>
          ))}
        </div>

        <div className="mt-4 grid gap-3">
          <Field label="Notes (observations, posture, lifestyle)">
            <textarea className="input min-h-[60px]" value={form.notes} onChange={e=>set('notes', e.target.value)} data-testid="fa-notes"/>
          </Field>
          <Field label="Goals for the next period">
            <textarea className="input min-h-[60px]" value={form.goals} onChange={e=>set('goals', e.target.value)} data-testid="fa-goals" placeholder="e.g. lose 3kg, complete 20 push-ups, 90s plank, run 5K"/>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-4">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="fa-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            Save assessment
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-fg-muted font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}


/**
 * printAssessment — separate PDF showing the body-metric assessment only.
 * No exercise list. Includes a history table of all previous assessments
 * for trend visibility.
 */
function printAssessment({ client, last, rows }) {
  if (!last || !client) return;
  const present = (k) => last[k] != null && last[k] !== '';
  const html = `
    <h1>Fitness assessment · ${client.full_name}</h1>
    <h2 class="muted">Assessment date: ${last.date}</h2>

    <div class="row" style="margin-top:24px;">
      <div>
        <h3>Client</h3>
        <div class="grid">
          <div><strong>Name</strong></div><div>${client.full_name}</div>
          <div><strong>Email</strong></div><div>${client.email}</div>
          ${client.phone ? `<div><strong>Phone</strong></div><div>${client.phone}</div>` : ''}
          ${client.dob ? `<div><strong>DOB</strong></div><div>${client.dob}</div>` : ''}
          ${client.sex ? `<div><strong>Sex</strong></div><div>${client.sex}</div>` : ''}
        </div>
      </div>
      <div>
        <h3>Goals</h3>
        <p>${(last.goals || '—').replace(/\n/g, '<br/>')}</p>
      </div>
    </div>

    <h3 style="margin-top:32px;">Body metrics</h3>
    <table style="margin-top:6px;">
      <thead><tr><th>Metric</th><th>Value</th></tr></thead>
      <tbody>
        ${METRICS.filter(m => present(m.k)).map(m =>
          `<tr><td>${m.label}</td><td><strong>${last[m.k]}</strong> ${m.unit}</td></tr>`
        ).join('')}
      </tbody>
    </table>

    ${last.notes ? `<div style="margin-top:18px;color:#4A524A;"><strong>Trainer notes:</strong> ${last.notes}</div>` : ''}

    ${rows.length > 1 ? `
      <h3 style="margin-top:28px;">Progression history</h3>
      <table>
        <thead><tr><th>Date</th>${CHARTABLE.map(k => `<th>${METRICS.find(m => m.k === k)?.label || k}</th>`).join('')}</tr></thead>
        <tbody>
          ${rows.map(r => `<tr><td>${r.date}</td>${CHARTABLE.map(k => `<td>${r[k] ?? '—'}</td>`).join('')}</tr>`).join('')}
        </tbody>
      </table>
    ` : ''}

    ${signatureBlock({ leftLabel: 'Trainer signature', rightLabel: 'Client signature', showStamp: true })}
    <div class="footer">${client.full_name} · Fitness assessment summary · Non-clinical document — distinct from any physiotherapy record.</div>
  `;
  printHtml(html, `Fitness assessment · ${client.full_name}`);
}


/**
 * printProgram — separate PDF showing the prescribed training program only.
 * Pulls exercises from the saved training program (no fallback rule engine —
 * the trainer is expected to build the program in the UI first).
 */
function printProgram({ client, last, program }) {
  if (!client || !program?.exercises?.length) return;
  const goals = last?.goals || '';
  const html = `
    <h1>Training program · ${client.full_name}</h1>
    <h2 class="muted">${program.title || 'Training program'} · Week starting ${program.week_starts_on || new Date().toLocaleDateString()}</h2>

    <div class="row" style="margin-top:24px;">
      <div>
        <h3>Client</h3>
        <div class="grid">
          <div><strong>Name</strong></div><div>${client.full_name}</div>
          <div><strong>Email</strong></div><div>${client.email}</div>
          ${last?.weight_kg ? `<div><strong>Weight</strong></div><div>${last.weight_kg} kg</div>` : ''}
          ${last?.resting_hr ? `<div><strong>Resting HR</strong></div><div>${last.resting_hr} bpm</div>` : ''}
          ${last?.vo2_max ? `<div><strong>VO₂ max</strong></div><div>${last.vo2_max}</div>` : ''}
        </div>
      </div>
      <div>
        <h3>Goals</h3>
        <p>${(goals || '—').replace(/\n/g, '<br/>')}</p>
        ${program.notes ? `<p style="margin-top:8px;"><strong>Program notes:</strong> ${program.notes}</p>` : ''}
      </div>
    </div>

    <h3 style="margin-top:28px;">Prescribed exercises (${program.exercises.length})</h3>
    <table style="margin-top:6px;">
      <thead><tr><th>Exercise</th><th>Primary muscles</th><th>Sets × Reps</th><th>Rest</th><th>Notes</th></tr></thead>
      <tbody>
        ${program.exercises.map(ex => `
          <tr>
            <td><strong>${ex.name || '—'}</strong>${ex.level ? `<br/><span class="muted">${ex.level}</span>` : ''}</td>
            <td class="muted">${(ex.primaryMuscles || []).join(', ') || '—'}</td>
            <td>${ex.sets || '—'} × ${ex.reps || '—'}</td>
            <td>${ex.rest || '—'}</td>
            <td class="muted">${ex.notes || ''}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <p style="margin-top:18px;color:#4A524A;font-size:11px;">
      Exercises sourced from the open-source <a href="https://github.com/yuhonas/free-exercise-db">yuhonas/free-exercise-db</a> library (CC0).
    </p>

    ${signatureBlock({ leftLabel: 'Trainer signature', rightLabel: 'Client signature', showStamp: true })}
    <div class="footer">${client.full_name} · Fitness training program · Non-clinical document — distinct from any physiotherapy treatment plan.</div>
  `;
  printHtml(html, `Training program · ${client.full_name}`);
}
